// Shared API types.
//
// This module is imported from both the React app (src/) and the Pages
// Functions (functions/). It must not import any runtime code that depends on
// either environment — types and pure constants only.

// ── Zasso voice ────────────────────────────────────────────────
// spec §2.7 / T7-1: 8 voices. Tsuyaku is NOT here — dynamic voice adaptation
// means the voice is not user-selectable (spec §1.2, §2.3.1).
export const ZASSO_VOICES = [
  "alloy",
  "verse",
  "coral",
  "sage",
  "ash",
  "ballad",
  "cedar",
  "marin",
] as const;
export type ZassoVoice = (typeof ZASSO_VOICES)[number];

// ── Okoshi transcription model (spec §2.3.2) ───────────────────
// 3 options, per live API verification (2026-05):
//   - gpt-4o-mini-transcribe  — 200 OK
//   - gpt-4o-transcribe       — 200 OK (default)
//   - gpt-realtime-whisper    — 200 OK, but the mint body must NOT include
//     `turn_detection` (OpenAI returns 400 "Turn detection is not supported
//     for this transcription model."). transcribe.ts special-cases this.
//
// `gpt-4o-transcribe-diarize` is intentionally excluded: OpenAI rejected it
// with 400 "Your organization does not have access to this transcription
// model." Add it back if/when access is granted.
export const OKOSHI_TRANSCRIBE_MODELS = [
  "gpt-4o-mini-transcribe",
  "gpt-4o-transcribe",
  "gpt-realtime-whisper",
] as const;
export type OkoshiTranscribeModel = (typeof OKOSHI_TRANSCRIBE_MODELS)[number];

// ── Tsuyaku output language (spec §1.2) ────────────────────────
// `gpt-realtime-translate` auto-detects source language from 70+ inputs and
// translates into one of these 13 supported output languages. The user picks
// the target language in Settings; the source language has no UI control.
//
// Reference: https://developers.openai.com/cookbook/examples/voice_solutions/realtime_translation_guide
//   "Realtime Translation currently supports 13 target output languages:
//    Spanish, Portuguese, French, Japanese, Russian, Chinese, German, Korean,
//    Hindi, Indonesian, Vietnamese, Italian, and English."
export const TSUYAKU_OUTPUT_LANGUAGES = [
  "en",
  "ja",
  "es",
  "pt",
  "fr",
  "de",
  "it",
  "ru",
  "zh",
  "ko",
  "hi",
  "id",
  "vi",
] as const;
export type TsuyakuOutputLanguage = (typeof TSUYAKU_OUTPUT_LANGUAGES)[number];

/** Native-name display labels for each output language code. */
export const TSUYAKU_LANGUAGE_LABELS: Record<TsuyakuOutputLanguage, string> = {
  en: "English",
  ja: "日本語",
  es: "Español",
  pt: "Português",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  ru: "Русский",
  zh: "中文",
  ko: "한국어",
  hi: "हिन्दी",
  id: "Bahasa Indonesia",
  vi: "Tiếng Việt",
};

// ── Vault save policy per channel ──────────────────────────────
// spec §2.5 + §1.3 U-OKO-3, §1.4 U-ZAS-6.
export const OKOSHI_VAULT_MODES = [
  "auto-save",
  "ask-each-time",
  "never",
] as const;
export type OkoshiVaultMode = (typeof OKOSHI_VAULT_MODES)[number];

export const ZASSO_VAULT_MODES = [
  "ask-each-time",
  "auto-save",
  "never",
] as const;
export type ZassoVaultMode = (typeof ZASSO_VAULT_MODES)[number];

// ── Full settings shape ────────────────────────────────────────
// spec §2.5: `settings:{user_email}` KV value.
export interface Settings {
  zassoVoice: ZassoVoice;
  /** @deprecated Phase 3 以降、デバイス選択は localStorage で管理。
   *  これらの KV フィールドは旧 spec の名残で、現在は読み書きしない。
   *  参照: src/lib/deviceStorage.ts */
  lastInputId: string | null;
  /** @deprecated Phase 3 以降、デバイス選択は localStorage で管理。
   *  これらの KV フィールドは旧 spec の名残で、現在は読み書きしない。
   *  参照: src/lib/deviceStorage.ts */
  lastOutputId: string | null;
  okoshiVaultMode: OkoshiVaultMode;
  zassoVaultMode: ZassoVaultMode;
  okoshiTranscribeModel: OkoshiTranscribeModel;
  /** Tsuyaku translation target language. Source language is auto-detected
   *  by gpt-realtime-translate from 70+ supported inputs. */
  tsuyakuOutputLanguage: TsuyakuOutputLanguage;
}

export const DEFAULT_SETTINGS: Settings = {
  zassoVoice: "verse",
  lastInputId: null,
  lastOutputId: null,
  // spec §1.3 U-OKO-3: user-driven confirmation is safer by default for
  // Okoshi — Vault writes are irreversible and the mic may have captured
  // unintended content. Users who want instant save switch to "auto-save".
  okoshiVaultMode: "ask-each-time",
  zassoVaultMode: "ask-each-time",
  okoshiTranscribeModel: "gpt-4o-transcribe",
  // English keeps backwards compatibility with the JA→EN default that was
  // hard-coded before configurable target languages.
  tsuyakuOutputLanguage: "en",
};

// ── Vault save endpoint payload ────────────────────────────────
// POST /api/vault/save — spec §2.6 / §2.6.1.
export const VAULT_SOURCES = ["Okoshi", "Zasso"] as const;
export type VaultSource = (typeof VAULT_SOURCES)[number];

export interface VaultSaveRequest {
  source: VaultSource;
  title?: string;
  content: string;
  /** ISO-8601 timestamp; used to derive the note path in JST. */
  dateTimeIso: string;
}

export interface VaultSaveResponse {
  savedPath: string;
  vaultUrl: string;
}

// ── Common error envelope ──────────────────────────────────────
// Follows spec §2.11 "ERR · <CODE> · 0x<HEX>".
export interface ApiErrorBody {
  error: {
    code: string;
    hex: string;
    message: string;
  };
}

// ── Tsuyaku session endpoint (spec §2.3.1 / §2.6) ──────────────
// POST /api/session/translate
export interface TranslateSessionRequest {
  output_language: TsuyakuOutputLanguage;
}

export interface TranslateSessionResponse {
  /** Ephemeral client secret ("ek_..."). */
  clientSecret: string;
  /** Expiry epoch seconds. */
  expiresAt: number;
  /** Model that will be used for the session. */
  model: string;
  /** Which ephemeral-token endpoint served the response. */
  endpointVariant: "translations" | "client_secrets";
  /** Initial session config echoed back from OpenAI (debug aid). */
  session?: Record<string, unknown>;
}

// ── Okoshi session endpoint (spec §2.3.2 / §2.6) ───────────────
// POST /api/session/transcribe
// Single endpoint (/v1/realtime/client_secrets with session.type:
// "transcription"); unlike Tsuyaku there is no primary/fallback split.
export interface TranscribeSessionRequest {
  model: OkoshiTranscribeModel;
}

export interface TranscribeSessionResponse {
  /** Ephemeral client secret ("ek_..."). */
  clientSecret: string;
  /** Expiry epoch seconds — matches TranslateSessionResponse.expiresAt. */
  expiresAt: number;
  /** Model that will be used for the session. */
  model: OkoshiTranscribeModel;
  /** Initial session config echoed back from OpenAI (debug aid). */
  session?: Record<string, unknown>;
}

// ── Zasso session endpoint (spec §2.3.3 / §2.6) ────────────────
// POST /api/session/chat
// Single endpoint (/v1/realtime/client_secrets with session.type: "realtime").
// Model is pinned to gpt-realtime-2 server-side — see spec §2.3.3. Voice is
// the only client-selectable parameter; instructions (§2.4.3) are injected
// server-side so the browser cannot override them.
export interface ChatSessionRequest {
  voice: ZassoVoice;
}

export interface ChatSessionResponse {
  /** Ephemeral client secret ("ek_..."). */
  clientSecret: string;
  /** Expiry epoch seconds. */
  expiresAt: number;
  /** Model that will be used for the session — always "gpt-realtime-2". */
  model: "gpt-realtime-2";
  /** Echo of the chosen voice for client-side verification. */
  voice: ZassoVoice;
  /** Initial session config echoed back from OpenAI (debug aid). */
  session?: Record<string, unknown>;
}

// ── Vault context endpoints (spec-001 §2.6) ────────────────────
// These endpoints back the Zasso "Vault Context" function tools. Unlike
// the §2.11 error catalog, they use a semantic envelope — HTTP 200 with
// an `error` string on failure — so the Realtime model can read the
// natural-language explanation directly.

// POST /api/vault/search
export interface VaultSearchRequest {
  query: string;
}

export interface VaultSearchResultItem {
  path: string;
  /** Already trimmed to a few lines on the Vault MCP side. */
  matches: string[];
  project?: string;
  source?: string;
  date?: string;
}

export interface VaultSearchResponse {
  results?: VaultSearchResultItem[];
  error?: "VAULT_TIMEOUT" | "VAULT_INTERNAL";
}

// POST /api/vault/read
export interface VaultReadRequest {
  path: string;
}

export interface VaultReadResponse {
  path?: string;
  frontmatter?: Record<string, unknown>;
  /** Body text only; Vault MCP caps at ~3000 chars and sets truncated=true
   *  when the original note was longer. */
  content?: string;
  truncated?: boolean;
  fullLength?: number;
  error?: "VAULT_TIMEOUT" | "VAULT_NOT_FOUND" | "VAULT_INTERNAL";
}
